
import { useState, useEffect, useMemo, memo, useCallback } from "react";
import Swal from "sweetalert2";
import AnimacionCarga from "../animacion_de_carga";
import TablaGenerica from "../tablas";
import BarraSuperior from "../barra_superior";
import "../../css/tickets.css";
import {
  obtenerSOS,
  suscribirSOS,
  gestionarSOS,
  cierreMasivoSOS,
  ESTADOS_SOS,
  normalizarEstado,
} from "./consultas_sos";

const TODOS = "__todos__";

const PALETA_SOS = {
  "Pendiente": { fondo: "#fdeceb", texto: "#b3261e", punto: "#e74c3c" },
  "Cerrado": { fondo: "#e6f7ed", texto: "#0d7a3d", punto: "#27ae60" },
};
const paletaSOS = (estado) => PALETA_SOS[estado] || PALETA_SOS["Pendiente"];

const ocultarNA = (valor) =>
  (!valor || valor === "N/A" || valor === "n/a") ? "" : valor;

const BadgeSOS = ({ estado }) => {
  const { fondo, texto, punto } = paletaSOS(normalizarEstado(estado));
  return (
    <span className="tk-badge" style={{ background: fondo, color: texto }}>
      <i className="bi bi-circle-fill" style={{ color: punto }} />
      {ocultarNA(estado) || "—"}
    </span>
  );
};

const soloFecha = (valor = "") => String(valor || "").substring(0, 10);

const ubicacionValida = (fila) => {
  const lat = Number(fila?.latitud);
  const lng = Number(fila?.longitud);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

  return { lat, lng };
};

const renderizarObservacion = (valor) => {
  if (!valor) return "";
  const lineas = valor.split("\n").filter((l) => l.trim());
  if (!lineas.length) return "";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: "200px", maxWidth: "320px" }}>
      {lineas.map((linea, i) => {
        const match = linea.match(/^\[(.+?) — (.+?)\] (.+)$/);
        if (!match) return (
          <div key={i} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "8px 10px", fontSize: "12px", color: "#374151" }}>
            {linea}
          </div>
        );
        const [, fecha, operador, texto] = match;
        return (
          <div key={i} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "8px 10px" }}>
            <span style={{ fontSize: "10px", color: "#9ca3af", display: "block", marginBottom: "3px" }}>
              {fecha} — {operador}
            </span>
            <span style={{ fontSize: "12px", color: "#374151" }}>{texto}</span>
          </div>
        );
      })}
    </div>
  );
};

const COLUMNAS = [
  { label: "Fecha Creación", key: "fecha_creacion" },
  { label: "Cliente", key: "cliente" },
  { label: "Nombre completo", key: "nombre_completo" },
  { label: "Teléfono", key: "telefono_final" },
  { label: "Estado", key: "estado_final" },
  { label: "Tipo", key: "tipo" },
  { label: "Fecha Soporte", key: "fecha_y_hora_soporte" },
  { label: "Observación", key: "observacion" },
  { label: "Tiempo de atención", key: "tiempo_atencion" },
];

const ListadoSOS = memo(() => {
  const [sos, setSos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [filtros, setFiltros] = useState({
    estado: TODOS, tipo: TODOS, cliente: TODOS, desde: "", hasta: "",
  });

  const aplicarLista = useCallback((lista) => {
    setSos(lista);
    setCargando(false);
  }, []);

  useEffect(() => {
    const cancelar = suscribirSOS(aplicarLista, () => setCargando(false));
    return cancelar;
  }, [aplicarLista]);

  const refrescar = useCallback(async () => {
    aplicarLista(await obtenerSOS());
  }, [aplicarLista]);

  const manejarGestionSOS = useCallback((fila) => {
    gestionarSOS(fila, refrescar);
  }, [refrescar]);


  const tipos = useMemo(
    () => [...new Set(sos.map((s) => s.tipo).filter((v) => v && v !== "N/A"))].sort(),
    [sos]
  );
  const clientes = useMemo(
    () => [...new Set(sos.map((s) => s.cliente).filter((v) => v && v !== "N/A"))].sort(),
    [sos]
  );


  const baseSinEstado = useMemo(() => {
    return sos.filter((s) => {
      if (filtros.tipo !== TODOS && s.tipo !== filtros.tipo) return false;
      if (filtros.cliente !== TODOS && s.cliente !== filtros.cliente) return false;

      const fecha = soloFecha(s.fecha_creacion);
      if (filtros.desde && fecha < filtros.desde) return false;
      if (filtros.hasta && fecha > filtros.hasta) return false;

      return true;
    });
  }, [sos, filtros.tipo, filtros.cliente, filtros.desde, filtros.hasta]);

  const sosVisibles = useMemo(() => (
    filtros.estado === TODOS
      ? baseSinEstado
      : baseSinEstado.filter((s) => normalizarEstado(s.estado_final) === filtros.estado)
  ), [baseSinEstado, filtros.estado]);

  const resumen = useMemo(() => {
    const conteo = ESTADOS_SOS.reduce((acc, e) => ({ ...acc, [e]: 0 }), {});
    baseSinEstado.forEach((s) => {
      const estado = normalizarEstado(s.estado_final);
      conteo[estado] = (conteo[estado] || 0) + 1;
    });
    return conteo;
  }, [baseSinEstado]);

  const manejarCierreMasivo = useCallback(() => {
    cierreMasivoSOS(sosVisibles, refrescar);
  }, [sosVisibles, refrescar]);

  const cambiarFiltro = (campo) => (e) => setFiltros((f) => ({ ...f, [campo]: e.target.value }));


  if (cargando) return <AnimacionCarga />;

  const botonesAccion = [
    { texto: "Gestionar SOS", icono: "bi-exclamation-triangle", accion: manejarGestionSOS },
    {
      texto: "Abrir Ubicación", icono: "bi-geo-alt", accion: (fila) => {
        const coordenadas = ubicacionValida(fila);
        if (!coordenadas) {
          Swal.fire({
            title: "Sin ubicación",
            text: "Esta alerta no trae coordenadas: el residente no dio permiso de ubicación o el GPS no respondió a tiempo.",
            icon: "info",
            confirmButtonText: "Entendido",
          });
          return;
        }
        window.open(
          `https://www.google.com/maps/search/?api=1&query=${coordenadas.lat},${coordenadas.lng}`,
          "_blank",
          "noopener,noreferrer"
        );
      },
    },
  ];

  const renderizadores = {
    cliente: ocultarNA,
    nit: ocultarNA,
    nombre_completo: ocultarNA,
    telefono_final: ocultarNA,
    tipo: ocultarNA,
    fecha_y_hora_soporte: ocultarNA,
    tiempo_atencion: ocultarNA,
    estado_final: (valor) => <BadgeSOS estado={valor} />,
    observacion: renderizarObservacion,
  };

  const totalBase = baseSinEstado.length;

  return (
    <div className="tk">
      <BarraSuperior />

      <div className="tk-cabecera-modulo">
        <div className="tk-encabezado">
          <div>
            <h1>Alertas SOS</h1>
            <p>{sosVisibles.length} de {sos.length} alertas</p>
          </div>

          <div className="tk-acciones">
            <button type="button" className="tk-btn tk-btn-icono" title="Refrescar" onClick={refrescar}>
              <i className="bi bi-arrow-clockwise" />
            </button>

            <button type="button" className="tk-btn" onClick={manejarCierreMasivo}>
              <i className="bi bi-check2-all" /> Cierre masivo
            </button>
          </div>
        </div>

        <div className="tk-kpis">
          <button
            type="button"
            className={`tk-kpi ${filtros.estado === TODOS ? "activa" : ""}`}
            onClick={() => setFiltros((f) => ({ ...f, estado: TODOS }))}
          >
            <span className="tk-kpi-etiqueta">
              <span className="tk-kpi-punto" style={{ background: "var(--tk-tinta)" }} />
              Todas
            </span>
            <span className="tk-kpi-valor">{totalBase}</span>
            <span className="tk-kpi-pie">Sin filtrar por estado</span>
          </button>

          {ESTADOS_SOS.map((estado) => (
            <button
              key={estado}
              type="button"
              className={`tk-kpi ${filtros.estado === estado ? "activa" : ""}`}
              onClick={() => setFiltros((f) => ({ ...f, estado: f.estado === estado ? TODOS : estado }))}
            >
              <span className="tk-kpi-etiqueta">
                <span className="tk-kpi-punto" style={{ background: paletaSOS(estado).punto }} />
                {estado}
              </span>
              <span className="tk-kpi-valor">{resumen[estado] || 0}</span>
              <span className="tk-kpi-pie">
                {totalBase ? Math.round(((resumen[estado] || 0) / totalBase) * 100) : 0}% de lo filtrado
              </span>
            </button>
          ))}
        </div>

        <div className="tk-filtros">
          <div className="tk-filtro">
            <label>Estado</label>
            <select value={filtros.estado} onChange={cambiarFiltro("estado")}>
              <option value={TODOS}>Todos</option>
              {ESTADOS_SOS.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>

          <div className="tk-filtro">
            <label>Cliente</label>
            <select value={filtros.cliente} onChange={cambiarFiltro("cliente")}>
              <option value={TODOS}>Todos</option>
              {clientes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="tk-filtro">
            <label>Tipo</label>
            <select value={filtros.tipo} onChange={cambiarFiltro("tipo")}>
              <option value={TODOS}>Todos</option>
              {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="tk-filtro">
            <label>Desde</label>
            <input type="date" value={filtros.desde} onChange={cambiarFiltro("desde")} />
          </div>

          <div className="tk-filtro">
            <label>Hasta</label>
            <input type="date" value={filtros.hasta} onChange={cambiarFiltro("hasta")} />
          </div>
        </div>
      </div>

      <TablaGenerica
        datos={sosVisibles}
        titulos={COLUMNAS.map((c) => c.label.toUpperCase())}
        campos={COLUMNAS.map((c) => c.key)}
        elementosPorPagina={500}
        columnaOrdenInicial={0}
        direccionOrdenInicial="desc"
        botones={botonesAccion}
        renderizadores={renderizadores}
        mostrarBarraSuperior={false}
      />
    </div>
  );
});

ListadoSOS.displayName = "ListadoSOS";

export default ListadoSOS;
